/* eslint-disable prettier/prettier */
import {
  defaultFormatToHumanDateFormat,
  defaultLocaleDateString,
} from 'core/constants/config.constants'
import { debugErrorLog } from '.'

const ymdFormat = /^\d{4}[-/.]\d{2}[-/.]\d{2}$/ // Y-m-d, Y/m/d, Y.m.d formats
const dmyFormat = /^\d{2}[-/.]\d{2}[-/.]\d{4}$/ // d-m-Y, d/m/Y, d.m.Y formats

function today(): string {
  const today = new Date()
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' }

  return today.toLocaleDateString(defaultLocaleDateString, options)
}

function isToday(date: Date | string): boolean {
  if (typeof date === 'string') {
    date = new Date(date)
  }
  const today = new Date()
  // Remove the time component from today's date
  const todayWithoutTime = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  // Remove the time component from the provided date
  const dateWithoutTime = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  // Compare the dates without time
  return todayWithoutTime.getTime() === dateWithoutTime.getTime()
}

function formatToHumanDate(dateString: string, withTime = false): string {
  const { day, month, year, hours, minutes, seconds } = extractDataPartsFromString(dateString)

  const date = new Date(Number(year), Number(month)-1, Number(day), Number(hours), Number(minutes), Number(seconds))

  let options: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' };
  if (withTime) {
    options = { ...options, hour: '2-digit', minute: '2-digit', second: '2-digit' };
  }
  const formattedDate = new Intl.DateTimeFormat(defaultFormatToHumanDateFormat, options).format(
    date,
  )

  return day + ' ' + formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)
}

function formatDateStringToInputDateFormat(dateString: string): string {
  const { day, month, year } = extractDataPartsFromString(dateString)

  // Return the date in Y-m-d format
  return `${year}-${Number(month)-1}-${day}`
}

function formatDateStringToDMY(dateString: string, splitter: string = '.'): string {
  const { day, month, year } = extractDataPartsFromString(dateString)

  // Return the date in d-m-Y format
  return `${day}${splitter}${Number(month)-1}${splitter}${year}`
}

function formatDateStringToMY(dateString: string, splitter: string = '.'): string {
  const { month, year } = extractDataPartsFromString(dateString)

  // Return the date in d-m-Y format
  return `${month}${splitter}${year}`
}

function extractDataPartsFromString(dateString: string): {
  day: string
  month: string
  year: string
  hours: string
  minutes: string
  seconds: string
} {
  // Remove time part if present
  let aux = dateString.split(' ')
  dateString = aux[0]
  let timeString = aux[1]
  aux = dateString.split('T')
  dateString = aux[0]
  timeString = aux[1]

  // Regular expressions for different date formats


  let dateParts
  let day, month, year

  if (ymdFormat.test(dateString)) {
    // Y-m-d format
    dateParts = dateString.split(/[-/.]/)
    year = dateParts[0]
    month = dateParts[1]
    day = dateParts[2]
  } else if (dmyFormat.test(dateString)) {
    // d-m-Y format
    dateParts = dateString.split(/[-/.]/)
    day = dateParts[0]
    month = dateParts[1]
    year = dateParts[2]
  } else {
    throw new Error(
      'Date format not recognized. Please use Y-m-d, Y/m/d, Y.m.d, d-m-Y, d/m/Y, or d.m.Y format.',
    )
  }
  // extract time parts
  let hours = '00'
  let minutes = '00'
  let seconds = '00'
  if (timeString) {
    aux = timeString.split(':')
    hours = aux[0]
    minutes = aux[1]
    seconds = aux[2].split('.')[0]
  }

  // Return the date in an object with day, month, and year
  return { day, month, year, hours, minutes, seconds }
}

function isDate(dateString: string): boolean {
  try {
    // Remove time part if present
    dateString = dateString.split(' ')[0]
    dateString = dateString.split('T')[0]
    return ymdFormat.test(dateString) || dmyFormat.test(dateString)
  } catch (error) {
    debugErrorLog('DateUtils isDateTime:', error)
    return false
  }
}

function stringToDate(dateString: string): Date | null {
  try {
    // Check if date string includes time part
    const includesTime = dateString.includes(' ')

    let dateParts
    let year,
      month,
      day,
      hours = 0,
      minutes = 0,
      seconds = 0

    if (ymdFormat.test(dateString)) {
      // Y-m-d format
      dateParts = dateString.split(/[-\s:]/)
      year = parseInt(dateParts[0], 10)
      month = parseInt(dateParts[1], 10) - 1 // JavaScript months are 0-based
      day = parseInt(dateParts[2], 10)
    } else if (dmyFormat.test(dateString)) {
      // d-m-Y format
      dateParts = dateString.split(/[-\s:]/)
      day = parseInt(dateParts[0], 10)
      month = parseInt(dateParts[1], 10) - 1 // JavaScript months are 0-based
      year = parseInt(dateParts[2], 10)
    } else {
      throw new Error('Date format not recognized. Please use Y-m-d or d-m-Y format.')
    }

    if (includesTime) {
      hours = parseInt(dateParts[3], 10)
      minutes = parseInt(dateParts[4], 10)
      seconds = parseInt(dateParts[5], 10)
    }

    return new Date(year, month, day, hours, minutes, seconds)
  } catch (error) {
    debugErrorLog('DateUtils stringToDate:', error)
    return null
  }
}

export {
  today,
  formatToHumanDate,
  stringToDate,
  isToday,
  formatDateStringToDMY,
  formatDateStringToMY,
  isDate as isDateTime,
  formatDateStringToInputDateFormat,
}
